<template>
  <WithAsideCards
    :data="data?.fields?.asides"
    data-documentation-download-v="3.5.0"
  >
    <div :class="$style.contentContainer">
      <div :class="$style.filtersBar">
        <div :class="$style.dataControls">
          <div
            v-if="data?.fields?.filterOptions?.length"
            :class="$style.brandFilters"
          >
            <BaseButton
              v-for="tag in data?.fields?.filterOptions"
              :key="tag?.sys?.id"
              :presentation="
                activeFilter?.sys?.id === tag?.sys?.id ? 'default' : 'clear'
              "
              :class="$style.brandFilter"
              @click="activeFilter = tag"
            >
              {{ tag?.fields?.label }}
            </BaseButton>
          </div>
          <div :class="$style.mobileBrandFilters">
            <BaseSelect
              v-model="selectedFilter"
              :class="$style.categories"
              :options="computedFilterOptions"
              :active-filter-value="computedActiveFilter"
            />
          </div>

          <SearchField
            :class="$style.search"
            :input-class="$style.searchInput"
            :placeholder="computedSearchPlaceholder"
            @request-query="(query) => (searchQuery = query)"
            @clear-query="searchQuery = ''"
          />
        </div>
      </div>

      <!-- <div v-if="fetchedData?.entries?.length" :class="$style.sortControls">
        <div :class="$style.sortImage"></div>
        <button
          :class="$style.sortTitle"
          @click="
            () => {
              if (sortBy === localizedStrings['title']) {
                sortAsc = !sortAsc
              }
              sortBy = localizedStrings['title']
            }
          "
        >
          {{ localizedStrings['title'] }}
          <template v-if="sortBy === localizedStrings['title']">
            <IconChevronUp v-if="!sortAsc" :class="$style.sortIcon" />
            <IconChevronDown v-else :class="$style.sortIcon" />
          </template>
        </button>
        <button
          :class="$style.sortUpdated"
          @click="
            () => {
              if (sortBy === localizedStrings['updated']) {
                sortAsc = !sortAsc
              }
              sortBy = localizedStrings['updated']
            }
          "
        >
          {{ localizedStrings['updated'] }}
          <template v-if="sortBy === localizedStrings['updated']">
            <IconChevronUp v-if="!sortAsc" :class="$style.sortIcon" />
            <IconChevronDown v-else :class="$style.sortIcon" />
          </template>
        </button>
        <div :class="$style.sortDownload"></div>
      </div> -->

      <div :class="$style.documentsWrapper">
        <div v-if="!computedVisibleEntries.length" :class="$style.noResults">
          {{ localizedStrings['no-results'] }}
        </div>
        <div :class="$style.documents">
          <DownloadableDocument
            v-for="document in computedVisibleEntries"
            :key="document?.sys?.id"
            :data="document"
            :class="$style.item"
            :set-name="data?.fields?.entryTitle"
          />
          <div
            v-if="computedVisibleEntries?.length < computedFilteredDocs?.length"
            :class="$style.loadMoreWrapper"
          >
            <BaseButton @click="onRequestMore">
              {{ localizedStrings['load-more'] || 'Load More' }}
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
  </WithAsideCards>
</template>

<script setup lang="ts">
import type {
  TypeDownloadableDocumentSkeleton,
  TypeEntriesCollectionWithoutUnresolvableLinksResponse,
  TypeTagWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

const { locale } = useI18n()
const { $client, $visibility } = useNuxtApp()
const { localizedStrings } = useLocalizedStrings()

const LOAD_COUNT = 10

// Props
const props = defineProps<{
  data: TypeEntriesCollectionWithoutUnresolvableLinksResponse
}>()

// Data
const activeFilter = ref<TypeTagWithoutUnresolvableLinksResponse | undefined>(
  props?.data?.fields?.filterOptions?.[0],
)
const selectedFilter = ref(activeFilter?.value?.sys?.id)
const sortBy = ref(localizedStrings.value['updated'])
const sortAsc = ref(true)
const searchQuery = ref('')
const renderedNum = ref(LOAD_COUNT)

// Async Data
const { data: fetchedData } = await useAsyncData(
  'downloadableDocuments',
  async () => {
    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeDownloadableDocumentSkeleton>(
        {
          content_type: 'downloadableDocument',
          include: 10,
          locale: locale.value,
          order: ['-fields.updated'],
        },
      )

    return response?.items?.filter?.((item) =>
      $visibility(item?.fields?.visibility),
    )
  },
  {
    transform(entries) {
      return {
        entries,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
  },
)

// Watch
watch(activeFilter, async () => {
  renderedNum.value = LOAD_COUNT

  // Keep selected category filter on desktop/mobile in sync
  if (activeFilter?.value?.sys?.id) {
    selectedFilter.value = activeFilter?.value?.sys?.id
  }
})
watch(selectedFilter, async () => {
  renderedNum.value = LOAD_COUNT

  // Keep selected category filter on desktop/mobile in sync
  const matchedFilter = props?.data?.fields?.filterOptions?.find(
    (tag) => tag?.sys?.id === selectedFilter.value,
  )
  if (matchedFilter) activeFilter.value = matchedFilter
})

// Computed
const computedSearchPlaceholder = computed(() => {
  return `${localizedStrings.value['search']} ${
    activeFilter?.value?.fields?.value === 'view-all'
      ? localizedStrings.value['all'] || 'All'
      : activeFilter?.value?.fields?.label
  }`
})

const computedFilterOptions = computed(() => {
  if (!props?.data?.fields?.filterOptions) return []

  return props.data.fields.filterOptions.map((tag) => {
    return {
      label: tag?.fields?.label || '',
      value: tag?.sys?.id || '',
    }
  })
})

const computedActiveFilter = computed(
  () =>
    computedFilterOptions.value.find(
      (tag) => tag.value === activeFilter.value?.sys?.id,
    )?.value || '',
)

const computedFilteredDocs = computed(() => {
  const ls = localizedStrings.value
  const filteredEntries = fetchedData?.value?.entries?.filter?.((d) => {
    const preferredBrandValue =
      d?.fields?.associatedBrand?.fields?.value ||
      d?.fields?.brand2?.fields?.value ||
      ''

    // Filter by brand
    let passesFilter = false
    if (activeFilter?.value?.fields?.value == 'view-all') {
      passesFilter = true
    } else if (preferredBrandValue === activeFilter?.value?.fields?.value) {
      passesFilter = true
    } else if (d?.fields?.brand === activeFilter?.value?.fields?.label) {
      passesFilter = true
    }

    // Filter by search query
    let passesSearchQuery = false
    if (
      d?.fields?.title
        ?.toLowerCase?.()
        .includes?.(searchQuery?.value?.toLowerCase?.())
    )
      passesSearchQuery = true

    return passesFilter && passesSearchQuery
  })

  // Sort entries
  // Updated/Ascending
  if (sortBy.value === ls['updated'] && sortAsc.value)
    return filteredEntries || []
  // Updated/Descending
  if (sortBy.value === ls['updated'] && !sortAsc.value)
    return filteredEntries?.reverse() || []
  // Title/Ascending
  const ascendingByTitle =
    filteredEntries?.slice?.()?.sort((a, b) => {
      const titleA = a?.fields?.title || ''
      const titleB = b?.fields?.title || ''
      return titleA?.localeCompare?.(titleB, locale?.value)
    }) || []
  if (sortBy.value === ls['title'] && sortAsc.value)
    return ascendingByTitle || []
  // Title/Descending
  if (sortBy.value === ls['title'] && !sortAsc.value)
    return ascendingByTitle?.reverse() || []

  return filteredEntries || []
})

const computedVisibleEntries = computed(() => {
  if (!computedFilteredDocs?.value?.length) return []
  return computedFilteredDocs?.value?.slice?.(0, renderedNum.value)
})

// Methods
const onRequestMore = () => (renderedNum.value += LOAD_COUNT)
</script>

<style lang="scss" module>
.contentContainer {
  background-color: var(--gray-1);
  min-width: 0;
}

.filtersBar {
  background-color: var(--white);
  box-shadow: 0 1px 3px var(--gray-4);
  padding: 10px 20px;
  margin-bottom: 20px;
}

.dataControls {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin: auto;
  max-width: 1310px;

  @include media('md') {
    gap: 10px;
  }
}

.search {
  --width: 100%;

  @include media('md') {
    max-width: 280px;
    min-width: 140px;
    flex: 1;
  }
}

.searchInput {
  text-overflow: ellipsis;
}

.sort {
  margin-bottom: 10px;
  width: 100%;

  @include media('xs') {
    display: none;
  }
}

.sortControls {
  display: none;

  @include media('xs') {
    align-items: center;
    background-color: var(--gray-8);
    color: var(--white);
    display: grid;
    font-size: to-rem(17px);
    line-height: math.div(27, 17);
    gap: 3%;
    grid-template-columns: 13.5% 1fr 20% 150px;
    padding: 9px 39px;
  }
}

.sortTitle,
.sortUpdated {
  align-items: center;
  background-color: transparent;
  border: none;
  color: inherit;
  display: flex;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  padding: 0;
}

.sortIcon {
  margin-left: 5px;
  width: 11px;
}

.documentsWrapper {
  @include media('sm') {
    margin: 0 auto;
    max-width: 1350px;
  }
}

.item {
  background-color: var(--gray-8);
  color: var(--white);
}

.item:nth-child(odd) {
  background-color: var(--gray-7);
}

.noResults {
  text-align: center;
  margin-bottom: 20px;
}

.documents {
  background-color: var(--white);
  box-shadow: 0 4px 13px rgba(#000, 0.06);
  margin: 20px;
}

.loadMoreWrapper {
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 30px 0;
  position: relative;

  &:before,
  &:after {
    content: '';
    background-color: var(--black-alpha10);
    height: 1px;
    position: absolute;
    width: calc(41% - 75px);
  }

  &:before {
    left: 6%;
  }

  &:after {
    right: 6%;
  }
}

.brandFilters {
  display: none;

  @include media('md') {
    display: block;
  }
}

.mobileBrandFilters {
  display: block;
  width: 100%;
  margin-bottom: 10px;

  @include media('md') {
    display: none;
  }
}

.brandFilter {
  --button-min-width: 110px !important;
}
</style>
